import { descriptorsForTemplate, itemDescriptorAnswer } from '../domain/descriptors';
import type { ReportBoundText } from '../domain/wordingGuard';
import type { EvidenceState } from '../state/evidenceStore';
import type { FindingState } from '../state/findingStore';
import type { InspectionState } from '../state/inspectionStore';

// ---------- NY F1 Round 4 (RP-02): what the report prints from the inspector's own text ----------
//
// The guard may only ever scan what the report actually PRINTS from the inspector's own text, so the
// enumeration mirrors the assembler (src/report/assembly.ts): findings, limitation and not-present
// notes, limited portions, area notes and names the inspector wrote, free-text descriptors, photo
// captions and the inspector's notes. Product wording — labels, the frozen limitation templates, the
// scope statement, the agreement terms — is never scanned, and neither is anything that stays inside
// the app (the private item note, the AI scan note, checklist hints, AI guidance).
//
// Two readers: the Final Review wording REMINDER (never a block, OWNER-CONTRACT §8.9) and the closeout
// check that the English client report carries no Chinese (src/state/closeout.ts). It lives in its own
// module so the closeout can read it without importing the assembler and the AI store.
export interface WordingScanDeps {
  inspection: InspectionState;
  findings: FindingState;
  evidence: Pick<EvidenceState, 'evidence'>;
}

export function reportBoundInspectorText(inspectionId: string, deps: WordingScanDeps): ReportBoundText[] {
  const captionById = new Map(deps.evidence.evidence.map((e) => [e.id, e.caption] as const));
  return reportBoundTextOf(inspectionId, deps.inspection, deps.findings, (evidenceId) => captionById.get(evidenceId) ?? '');
}

export function reportBoundTextOf(inspectionId: string, inspectionState: InspectionState, findingState: FindingState, captionOf: (evidenceId: string) => string): ReportBoundText[] {
  const inspection = inspectionState.inspections.find((i) => i.id === inspectionId);
  if (!inspection) return [];
  const out: ReportBoundText[] = [];
  const push = (source: ReportBoundText['source'], text: string, areaId: string | null = null, itemId: string | null = null, findingId: string | null = null) => {
    if (text.trim().length > 0) out.push({ source, text, areaId, itemId, findingId });
  };
  push('inspectorNotes', inspection.notes);
  push('weather', inspection.weather);
  const areas = inspectionState.areas.filter((a) => a.inspectionId === inspectionId && a.enabled).sort((a, b) => a.sortOrder - b.sortOrder);
  const items = inspectionState.items.filter((i) => i.inspectionId === inspectionId);
  const confirmed = findingState.findings.filter((f) => f.inspectionId === inspectionId && f.status === 'confirmed');
  for (const area of areas) {
    // A plan-generated area name is product wording; a name the inspector typed prints as written.
    if (area.label === null) push('areaName', area.name, area.id);
    push('areaNote', area.note, area.id);
    for (const item of items.filter((i) => i.areaId === area.id).sort((a, b) => a.sortOrder - b.sortOrder)) {
      if (item.templateId === null) push('itemTitle', item.title, area.id, item.id);
      if (item.status === 'notApplicable' || (item.status === 'unableToInspect' && item.reasonCode === 'notInstalled')) {
        push('notPresentNote', item.statusReason, area.id, item.id);
        continue;
      }
      if (item.status === 'unableToInspect') {
        push('limitationNote', item.statusReason, area.id, item.id);
        continue;
      }
      for (const portion of item.limitedPortions) {
        push('portionExtent', portion.extent, area.id, item.id);
        push('portionNote', portion.note, area.id, item.id);
      }
      for (const definition of descriptorsForTemplate(item.templateId)) {
        const answer = itemDescriptorAnswer(item, definition.key);
        if (!answer) continue;
        // A chosen value of a CHOICE descriptor prints as a product label; only what the inspector
        // typed is theirs.
        if (definition.kind === 'text') push('descriptorValue', answer.value, area.id, item.id);
        push('descriptorValue', answer.detail, area.id, item.id);
      }
      // ⑥ A: an English report sentence the inspector wrote or edited is the inspector's text (CA-13).
      if (item.reportSentence !== null && item.reportSentence.source === 'inspector') push('reportSentence', item.reportSentence.en, area.id, item.id);
    }
    for (const finding of confirmed.filter((f) => f.areaId === area.id)) {
      push('findingTitle', finding.title, area.id, null, finding.id);
      push('findingObservation', finding.observation, area.id, null, finding.id);
      push('findingRecommendation', finding.recommendation, area.id, null, finding.id);
      if (finding.responsibleTrade === 'other') push('findingTrade', finding.responsibleTradeOther, area.id, null, finding.id);
      for (const evidenceId of finding.evidenceIds) push('photoCaption', captionOf(evidenceId), area.id, null, finding.id);
    }
  }
  return out;
}

// Chinese the English client report must not carry: Han characters and CJK / full-width punctuation
// (the same range the report's line flow treats as CJK, src/report/flowText.ts).
const CHINESE = /[⺀-鿿豈-﫿＀-￯　-〿]/;

export function containsChinese(text: string): boolean {
  return CHINESE.test(text);
}
